//#region src/surfaces/root-shell-apps.ts
/**
* Which apps may publish a root surface, and which hostnames an org may assign one to (layered
* shell surfaces, D2, D6, D20, D24, R-12 e, R-17, R-99).
*
* `ROOT_SHELL_APPS` is code, reviewed like `FIRST_PARTY_APPS`, so a stolen operator session
* cannot make an app root-eligible. The publish door (WS-4, through WS-1's
* `checkRootEligibility`), the CP tenant shell write (WS-28) and the DP root-shell PUT (WS-5)
* read it. `refusalFor` and the host cap are the hostname rules core-api runs at
* `PUT /v1/admin/root-shell` and the operator runs when it plans listeners (WS-5, WS-17), so the
* two never disagree on which name is assignable.
*/
/**
* The registered app slugs (`release.apps.slug`) that may publish a `root: true` surface:
* `default-shell`, the platform default (D24, D36), and `sdk-smoke`, the second root the
* acceptance probe assigns at `/alt` (R-17). Compared case-sensitively against the registered
* slug, never the manifest's `appId`.
*/
const ROOT_SHELL_APPS = Object.freeze(["default-shell", "sdk-smoke"]);
/** The most hostname assignments one org may hold (R-12 e). WS-5 refuses the fifth at PUT; the operator plans at most this many listeners per org. */
const MAX_ROOT_SHELL_HOSTS_PER_ORG = 4;
/** The longest hostname, in characters (RFC 1035's 253). */
const ROOT_SHELL_HOST_MAX_LENGTH = 253;
/**
* Name suffixes no tenant hostname may end in: one-label and two-label endings that resolve
* inside a cluster or a LAN. Compared against a host's last label and its last two labels.
*/
const RESERVED_HOST_SUFFIXES = Object.freeze([
	"cluster.local",
	"internal",
	"local",
	"localhost",
	"svc"
]);
const HOST_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const ALL_DIGITS = /^[0-9]+$/;
/**
* True when `apexes` holds at least one entry and every entry, lowercased, is one or more labels
* in {@link HOST_LABEL} within {@link ROOT_SHELL_HOST_MAX_LENGTH}. Pure; never throws.
*/
function reservedApexesUsable(apexes) {
	if (apexes.length === 0) return false;
	return apexes.every((raw) => {
		const apex = raw.toLowerCase();
		return apex.length <= 253 && apex.split(".").every((label) => HOST_LABEL.test(label));
	});
}
/**
* Why `host` cannot be a root-shell hostname on this DP, or `null` when it can.
*
* `host` is untrusted input from the admin PUT, already lowercased by the caller; an uppercase
* letter is a `grammar` refusal. The checks run in this order and the first match wins:
* 1. `grammar`: empty; longer than {@link ROOT_SHELL_HOST_MAX_LENGTH}; fewer than two labels; a
*    label outside `[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?`, which covers a trailing dot, an empty
*    label and a `*`; or an all-digit last label, the shape of an IPv4 literal.
* 2. `platform-domain`: equal to, or under, any entry of `ctx.reservedApexes` (R-99). An empty
*    list, an empty entry or an entry outside the label grammar refuses every host here (refuse
*    over accept), so a misconfigured list never lets a platform name through.
* 3. `chart-hostname`: equal to `ctx.orgSubdomain` or to any `ctx.chartHostnames` entry.
* 4. `reserved-suffix`: the last label, or the last two labels, is a {@link RESERVED_HOST_SUFFIXES} entry.
*
* Context values are compared lowercased. Pure; never throws.
*/
function refusalFor(host, ctx) {
	if (host.length === 0 || host.length > 253) return "grammar";
	const labels = host.split(".");
	if (labels.length < 2) return "grammar";
	if (!labels.every((label) => HOST_LABEL.test(label))) return "grammar";
	if (ALL_DIGITS.test(labels[labels.length - 1])) return "grammar";
	if (!reservedApexesUsable(ctx.reservedApexes)) return "platform-domain";
	if (ctx.reservedApexes.map((apex) => apex.toLowerCase()).some((apex) => host === apex || host.endsWith(`.${apex}`))) return "platform-domain";
	if ([ctx.orgSubdomain, ...ctx.chartHostnames].map((name) => name.toLowerCase()).includes(host)) return "chart-hostname";
	const lastOne = labels[labels.length - 1];
	const lastTwo = labels.slice(-2).join(".");
	if (RESERVED_HOST_SUFFIXES.includes(lastOne) || RESERVED_HOST_SUFFIXES.includes(lastTwo)) return "reserved-suffix";
	return null;
}
//#endregion
export { MAX_ROOT_SHELL_HOSTS_PER_ORG, RESERVED_HOST_SUFFIXES, ROOT_SHELL_APPS, ROOT_SHELL_HOST_MAX_LENGTH, refusalFor };

//# sourceMappingURL=root-shell-apps.js.map